import { useState, useEffect, useCallback } from "react";
import {
  listDocumentCategories,
  listDesignDisciplines,
  listDesignStages,
  type DesignDisciplineRow,
  type DesignStageRow,
} from "@/stores/admin/repository";
import type { DocumentCategoryRow } from "@/types/documents";

interface UseTeachFranklinDataReturn {
  categories: DocumentCategoryRow[];
  disciplines: DesignDisciplineRow[];
  stages: DesignStageRow[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to load all available tag options for the Teach Franklin modal.
 * Fetches categories, design disciplines, and design stages.
 */
export function useTeachFranklinData(): UseTeachFranklinDataReturn {
  const [categories, setCategories] = useState<DocumentCategoryRow[]>([]);
  const [disciplines, setDisciplines] = useState<DesignDisciplineRow[]>([]);
  const [stages, setStages] = useState<DesignStageRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [categoriesData, disciplinesData, stagesData] = await Promise.all([
        listDocumentCategories(),
        listDesignDisciplines(),
        listDesignStages(),
      ]);

      setCategories(categoriesData);
      setDisciplines(disciplinesData);
      setStages(stagesData);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load tag options";
      setError(message);
      console.error("useTeachFranklinData: Error loading data", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    categories,
    disciplines,
    stages,
    isLoading,
    error,
    refetch: fetchData,
  };
}
